import React, { useState, useCallback, useMemo } from 'react';
import { Column, Row, Text, Expander } from '@amzn/meridian';

import { getCycleDisplayName } from '../../utils/helpers';
import { getProcessOverlapInfo } from './VolumeDistributionCalculator';
import { getCycleTotalVolume, computeAbsoluteVolumes, formatEpochAsHHMM } from './FlowPlannerModel';
import { buildAdherenceActuals, matchProcessActuals } from './flowPlannerAdherenceModel';
import { PROCESS_TO_ADHERENCE } from './FlowPlannerGridConfig';
import { ActualFragment, actualAt } from './shared/actuals';
import ConfigPanel from './shared/ConfigPanel';

import './VolumeDistribution.css';

const PROCESSES = ['INDUCT', 'STOW', 'PICK'];

// Colors track CHART_COLORS in chartUtils (Pick = red). Induct is teal here so it
// stays distinct from the actuals accent blue beside these swatches.
export const PROCESS_CANONICAL = {
  INDUCT: '#0891b2',
  STOW: '#16a34a',
  PICK: '#ef4444'
};
// Classic palette: the strip takes the row's process colour and breaks are two
// shades of amber. Left exactly as it was.
const BREAK_PAID_CLASSIC = '#f59e0b';
const BREAK_UNPAID_CLASSIC = '#b45309';

// New labor plan grid: one colour meaning "working", since the row already says which
// process it is, and breaks that differ in hue rather than lightness - at 4px tall a
// light/dark pair of the same hue is indistinguishable.
export const WORKING_COLOR = '#0891b2';
export const BREAK_PAID = '#7c3aed';
export const BREAK_UNPAID = '#f59e0b';

// Per-interval volumes for a single cycle+process (override volumes, or derived
// from percentages × cycle total). Shared by both total aggregators below.
const cycleProcessVolumes = (cycleName, process, totalVolume, distributions, intervals) => {
  const cd = totalVolume.find((v) => v.name === cycleName);
  const cycleTotal = cd ? getCycleTotalVolume(cd) : 0;
  return (
    distributions[cycleName]?.[process]?.volumes ||
    computeAbsoluteVolumes(distributions[cycleName]?.[process]?.percentages || [], cycleTotal)
  );
};

// Process-grouped header totals: sum one process across all cycles, per interval.
const computeSlotTotals = (availableCycles, totalVolume, distributions, process, intervals) => {
  if (!availableCycles?.length || !totalVolume?.length || !intervals?.length) return [];
  return intervals.map((_, slotIndex) =>
    availableCycles.reduce(
      (sum, cn) => sum + (cycleProcessVolumes(cn, process, totalVolume, distributions, intervals)[slotIndex] || 0),
      0
    )
  );
};

// Cycle-grouped header totals: sum all processes within one cycle, per interval.
const computeCycleSlotTotals = (cycleName, totalVolume, distributions, intervals) => {
  if (!totalVolume?.length || !intervals?.length) return [];
  return intervals.map((_, slotIndex) =>
    PROCESSES.reduce(
      (sum, process) =>
        sum + (cycleProcessVolumes(cycleName, process, totalVolume, distributions, intervals)[slotIndex] || 0),
      0
    )
  );
};

/**
 * Derive the per-interval active cells for one cycle+process. Returns the cycle
 * total plus a sparse list of { slotIndex, interval, pct, volume, isAuto } for
 * the intervals the process actually spans.
 */
export const getActiveCells = (cycleName, process, intervals, distributions, totalVolume, processEvents, readOnly) => {
  const processEvent = processEvents?.[cycleName]?.[process];
  const cd = totalVolume?.find((v) => v.name === cycleName);
  const cycleTotalVolume = cd ? getCycleTotalVolume(cd) : 0;
  if (!processEvent?.length) return { cycleTotalVolume, processEvent: null, cells: [], activeSlotIndices: [] };

  const volumes =
    distributions[cycleName]?.[process]?.volumes ||
    computeAbsoluteVolumes(distributions[cycleName]?.[process]?.percentages || [], cycleTotalVolume);

  const activeSlotIndices = intervals
    .map((interval, idx) => (getProcessOverlapInfo(processEvent, interval).hasOverlap ? idx : -1))
    .filter((idx) => idx !== -1);
  const lastActiveIdx = activeSlotIndices.length ? activeSlotIndices[activeSlotIndices.length - 1] : -1;

  const cells = activeSlotIndices.map((slotIndex) => ({
    slotIndex,
    interval: intervals[slotIndex],
    pct: parseFloat(distributions[cycleName]?.[process]?.percentages?.[slotIndex]) || 0,
    volume: volumes[slotIndex] || 0,
    isAuto: readOnly || slotIndex === lastActiveIdx
  }));

  return { cycleTotalVolume, processEvent, activeSlotIndices, cells };
};

const formatRowWindow = (processEvent) => {
  const events = Array.isArray(processEvent) ? processEvent : processEvent ? [processEvent] : [];
  const firstStart = events[0]?.timeIntervals?.[0]?.startTime;
  const lastEnd = events[events.length - 1]?.timeIntervals?.[0]?.endTime;
  if (!firstStart || !lastEnd) return '';
  return `${formatEpochAsHHMM(firstStart)} – ${formatEpochAsHHMM(lastEnd)}`;
};

// Where, within this interval, the process actually runs — so the strip fill
// shows a process that starts partway through (e.g. Sort 0 mid-P2), not just
// a full-width bar.
const calculateBarPosition = (processEvents, interval) => {
  const events = Array.isArray(processEvents) ? processEvents : processEvents ? [processEvents] : [];
  const intervalDuration = interval.endTime - interval.startTime;
  const intervalStart = interval.startTime;
  for (const event of events) {
    if (!event?.timeIntervals?.[0]) continue;
    const processStart = Math.max(event.timeIntervals[0].startTime, intervalStart);
    const processEnd = Math.min(event.timeIntervals[0].endTime, interval.endTime);
    if (processEnd > processStart) {
      return {
        left: ((processStart - intervalStart) / intervalDuration) * 100,
        width: ((processEnd - processStart) / intervalDuration) * 100
      };
    }
  }
  return null;
};

const calculateBreakPositions = (interval) => {
  if (!interval.breaks || interval.breaks.length === 0) return [];
  const intervalDuration = interval.endTime - interval.startTime;
  const intervalStart = interval.startTime;
  return interval.breaks
    .map((brk) => {
      const breakStart = Math.max(brk.startTime, intervalStart);
      const breakEnd = Math.min(brk.endTime, interval.endTime);
      if (breakEnd <= breakStart) return null;
      return {
        left: ((breakStart - intervalStart) / intervalDuration) * 100,
        width: ((breakEnd - breakStart) / intervalDuration) * 100,
        type: brk.breakType
      };
    })
    .filter(Boolean);
};

// ─── Matrix sub-components ────────────────────────────────────────────────────

export const MatrixPercentInput = ({ pct, isAuto, onChange }) => {
  const [isFocused, setIsFocused] = useState(false);
  const display = isFocused ? pct : (parseFloat(pct) || 0).toFixed(2);
  if (isAuto) {
    return (
      <span className="vd-mtx-pct">
        {(parseFloat(pct) || 0).toFixed(2)}% <span className="vd-mtx-auto">auto</span>
      </span>
    );
  }
  return (
    <span className="vd-mtx-pct vd-mtx-pct-edit">
      <input
        type="text"
        value={display}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
      <span className="vd-mtx-pct-suffix">%</span>
    </span>
  );
};

// Slim timing strip — process span (positioned within the interval) + paid/
// unpaid breaks, replacing the heavy meter from the old grid while preserving
// the within-interval timing.
export const MeterStrip = ({
  interval,
  processEvent,
  color,
  breakPaid = BREAK_PAID_CLASSIC,
  breakUnpaid = BREAK_UNPAID_CLASSIC
}) => {
  const bar = calculateBarPosition(processEvent, interval);
  const breaks = calculateBreakPositions(interval);
  if (!bar && !breaks.length) return <div className="vd-mtx-strip" />;
  return (
    <div className="vd-mtx-strip">
      {bar && (
        <span
          className="vd-mtx-strip-fill"
          style={{ left: `${bar.left}%`, width: `${bar.width}%`, background: color }}
        />
      )}
      {breaks.map((b, i) => (
        <span
          key={i}
          className="vd-mtx-strip-break"
          style={{
            left: `${b.left}%`,
            width: `${b.width}%`,
            background: b.type === 'UNPAID' ? breakUnpaid : breakPaid
          }}
        />
      ))}
    </div>
  );
};

export const MatrixCell = ({
  cell,
  color,
  interval,
  processEvent,
  onChange,
  actual,
  showActuals = false,
  struck = false
}) => {
  const actualVolume = showActuals ? actual?.volume : undefined;
  if (!cell && actualVolume === undefined) return <td className="vd-mtx-cell vd-mtx-cell-empty">—</td>;

  const plannedVolume = cell?.volume ?? 0;

  return (
    <td className="vd-mtx-cell">
      <div className="vd-mtx-vol">
        <ActualFragment value={actualVolume} format={(v) => v.toLocaleString()} />
        <span className={struck ? 'fp-plan-struck' : undefined}>{plannedVolume.toLocaleString()}</span>
      </div>
      {cell && (
        <>
          <MatrixPercentInput pct={cell.pct} isAuto={cell.isAuto} onChange={onChange} />
          <MeterStrip interval={interval} processEvent={processEvent} color={color} />
        </>
      )}
    </td>
  );
};

const MatrixRowLabel = ({ name, color, volume, timeWindow }) => (
  <td className="vd-mtx-rlabel">
    <span className="vd-mtx-rdot" style={{ background: color }} />
    <span className="vd-mtx-rname">{name}</span>
    <div className="vd-mtx-rmeta">
      {volume.toLocaleString()}
      {timeWindow ? <span className="vd-mtx-rtime"> · {timeWindow}</span> : null}
    </div>
  </td>
);

const MatrixHeader = ({ intervals, labelText }) => (
  <tr>
    <th className="vd-mtx-th vd-mtx-th-label">{labelText}</th>
    {intervals.map((iv, idx) => (
      <th key={idx} className="vd-mtx-th vd-mtx-th-slot">
        <span className="vd-mtx-slot-line">
          <span className="vd-mtx-slot-primary">{iv.name}</span>
          <span className="vd-mtx-slot-secondary">
            {formatEpochAsHHMM(iv.startTime)}–{formatEpochAsHHMM(iv.endTime)}
          </span>
        </span>
      </th>
    ))}
  </tr>
);

// Group header row (the bold INDUCT/STOW/PICK or cycle-name band) plus its
// per-interval totals.
const MatrixGroupRow = ({ title, color, grandTotal, intervalTotals, intervals }) => (
  <tr className="vd-mtx-grp">
    <td>
      <span className="vd-mtx-grp-name">
        {color && <span className="vd-mtx-grp-dot" style={{ background: color }} />}
        {title}
        {grandTotal > 0 && <span className="vd-mtx-grp-total-inline"> ({grandTotal.toLocaleString()})</span>}
      </span>
    </td>
    {intervals.map((_, idx) => (
      <td key={idx} className="vd-mtx-grp-total">
        {intervalTotals[idx] > 0 ? intervalTotals[idx].toLocaleString() : ''}
      </td>
    ))}
  </tr>
);

// One cycle+process data row (label + per-interval cells). `rowName`/`rowColor`
// adapt to whichever dimension is the row in the active grouping.
// Actual volume series for one cycle+process, or null when unavailable.
const getRowActuals = (actualsForCycle, process) => {
  if (!actualsForCycle?.byProcess) return null;
  const matched = matchProcessActuals(actualsForCycle.byProcess, [PROCESS_TO_ADHERENCE[process]]);
  if (!matched?.volume) return null;
  return { volumes: matched.volume };
};

const MatrixDataRow = ({
  cycleName,
  process,
  rowName,
  rowColor,
  cellData,
  intervals,
  onPercentageChange,
  actualsForCycle,
  showActuals,
  endedByInterval = []
}) => {
  const { cells, cycleTotalVolume, processEvent, activeSlotIndices } = cellData;
  const bySlot = new Map(cells.map((c) => [c.slotIndex, c]));
  const rowActuals = getRowActuals(actualsForCycle, process);
  const actualForSlot = (idx) => (rowActuals ? { volume: actualAt(rowActuals.volumes, idx) } : null);
  return (
    <tr>
      <MatrixRowLabel
        name={rowName}
        color={rowColor}
        volume={cycleTotalVolume}
        timeWindow={formatRowWindow(processEvent)}
      />
      {intervals.map((interval, idx) => {
        const cell = bySlot.get(idx);
        const actual = actualForSlot(idx);
        return (
          <MatrixCell
            key={idx}
            cell={cell}
            interval={interval}
            processEvent={processEvent}
            color={rowColor}
            onChange={(value) => onPercentageChange(cycleName, process, idx, value, activeSlotIndices)}
            actual={actual}
            showActuals={showActuals}
            struck={!!cell && !!actual && !!endedByInterval[idx]}
          />
        );
      })}
    </tr>
  );
};

const MatrixView = ({
  intervals,
  availableCycles,
  totalVolume,
  distributions,
  processEvents,
  onPercentageChange,
  readOnly,
  groupBy = 'process',
  actualsByCycle = null,
  endedByInterval = []
}) => {
  const cells = (cycleName, process) =>
    getActiveCells(cycleName, process, intervals, distributions, totalVolume, processEvents, readOnly);

  // Each grouping is the same data pivoted: outer = group header, inner = data rows.
  const groups =
    groupBy === 'cycle'
      ? availableCycles.map((cycleName) => ({
          key: cycleName,
          title: getCycleDisplayName(cycleName),
          color: null,
          intervalTotals: computeCycleSlotTotals(cycleName, totalVolume, distributions, intervals),
          rows: PROCESSES.map((process) => ({
            cycleName,
            process,
            rowName: process,
            rowColor: PROCESS_CANONICAL[process],
            cellData: cells(cycleName, process)
          }))
        }))
      : PROCESSES.map((process) => ({
          key: process,
          title: process,
          color: PROCESS_CANONICAL[process],
          intervalTotals: computeSlotTotals(availableCycles, totalVolume, distributions, process, intervals),
          rows: availableCycles.map((cycleName) => ({
            cycleName,
            process,
            rowName: getCycleDisplayName(cycleName),
            rowColor: PROCESS_CANONICAL[process],
            cellData: cells(cycleName, process)
          }))
        }));

  const labelText = groupBy === 'cycle' ? 'Cycle / Process' : 'Process / Cycle';

  return (
    <table className="vd-mtx">
      <thead>
        <MatrixHeader intervals={intervals} labelText={labelText} />
      </thead>
      <tbody>
        {groups.map((group) => {
          const visibleRows = group.rows.filter((r) => r.cellData.cells.length);
          if (!visibleRows.length) return null;
          const grandTotal = group.intervalTotals.reduce((s, v) => s + v, 0);
          return (
            <React.Fragment key={group.key}>
              <MatrixGroupRow
                title={group.title}
                color={group.color}
                grandTotal={grandTotal}
                intervalTotals={group.intervalTotals}
                intervals={intervals}
              />
              {visibleRows.map((row) => (
                <MatrixDataRow
                  key={`${group.key}-${row.cycleName}-${row.process}`}
                  cycleName={row.cycleName}
                  process={row.process}
                  rowName={row.rowName}
                  rowColor={row.rowColor}
                  cellData={row.cellData}
                  intervals={intervals}
                  onPercentageChange={onPercentageChange}
                  actualsForCycle={actualsByCycle?.[row.cycleName] || null}
                  showActuals={actualsByCycle !== null}
                  endedByInterval={endedByInterval}
                />
              ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

// Revert-to-original control. `compact` is the icon-only variant shown in the
// collapsed summary; the default is the bordered "↩ Revert" button.
const RevertButton = ({ onClick, compact = false }) => (
  <button
    type="button"
    className={`vd-revert${compact ? ' vd-revert-compact' : ''}`}
    onClick={onClick}
    title="Revert to original distribution">
    ↩{compact ? '' : ' Revert'}
  </button>
);

// Sets one slot and re-derives the last active slot so the row still totals 100%.
// Shared with the folded-in editor in LaborPlanGrid so both layouts rebalance alike.
export function applyRemainder(percentages, slotIndex, value, activeSlotIndices) {
  const next = [...(percentages || [])];
  next[slotIndex] = value;
  if (activeSlotIndices && activeSlotIndices.length > 1) {
    const lastIdx = activeSlotIndices[activeSlotIndices.length - 1];
    if (slotIndex !== lastIdx) {
      const otherSum = activeSlotIndices
        .filter((idx) => idx !== lastIdx)
        .reduce((sum, idx) => sum + (parseFloat(next[idx]) || 0), 0);
      next[lastIdx] = Math.max(0, Math.round((100 - otherSum) * 100) / 100);
    }
  }
  return next;
}

const LEGEND_SWATCH = { width: 14, height: 14, borderRadius: 3, flexShrink: 0 };
const LEGEND_LABEL = { fontSize: '13px', color: '#5a6470', lineHeight: 1 };

const ProcessLegend = () => (
  <Row spacing="300" alignmentVertical="center">
    {[
      { color: PROCESS_CANONICAL.INDUCT, label: 'Induct' },
      { color: PROCESS_CANONICAL.STOW, label: 'Stow' },
      { color: PROCESS_CANONICAL.PICK, label: 'Pick' },
      // Classic legend: must match MeterStrip's classic break fills, not the new-view palette.
      { color: BREAK_PAID_CLASSIC, label: 'Paid break' },
      { color: BREAK_UNPAID_CLASSIC, label: 'Unpaid break' }
    ].map(({ color, label }) => (
      <Row key={label} spacing="100" alignmentVertical="center">
        <div style={{ ...LEGEND_SWATCH, backgroundColor: color }} />
        <span style={LEGEND_LABEL}>{label}</span>
      </Row>
    ))}
  </Row>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════════

const VolumeDistribution = ({
  intervals,
  availableCycles,
  totalVolume,
  volumeDistributions,
  onDistributionsChange,
  processEvents,
  readOnly = false,
  isOverridden = false,
  onRevert,
  joined = false,
  adherenceResponse = null,
  showActuals = false,
  endedByInterval = []
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // 'process' → group by process (cycles as rows); 'cycle' → group by cycle (processes as rows).
  const [groupBy, setGroupBy] = useState('process');

  // Volume is cycle-scoped, so build each cycle independently.
  const actualsByCycle = useMemo(() => {
    if (!showActuals || !adherenceResponse) return null;
    const out = {};
    (availableCycles || []).forEach((cycleName) => {
      out[cycleName] = buildAdherenceActuals(adherenceResponse, intervals, cycleName);
    });
    return out;
  }, [showActuals, adherenceResponse, availableCycles, intervals]);

  const hasActuals = useMemo(() => Object.values(actualsByCycle || {}).some((a) => a?.hasData), [actualsByCycle]);

  const handlePercentageChange = useCallback(
    (cycleName, process, slotIndex, value, activeSlotIndices) => {
      const current = volumeDistributions[cycleName]?.[process]?.percentages;
      onDistributionsChange(cycleName, process, applyRemainder(current, slotIndex, value, activeSlotIndices));
    },
    [volumeDistributions, onDistributionsChange]
  );

  return (
    <ConfigPanel joined={joined} className="vd-config">
      <Row widths={['fill', 'fit']} alignmentVertical="center">
        <Column spacing="300" alignmentHorizontal="stretch" width="100%">
          <Expander
            open={isExpanded}
            onChange={setIsExpanded}
            title={
              hasActuals ? (
                <>
                  Volume Distribution (<span className="vd-title-actual">Actual</span> / Planned)
                </>
              ) : (
                'Volume Distribution'
              )
            }
            type="inline"
            spacingInset="300">
            <Column spacing="300" alignmentHorizontal="stretch" width="100%">
              {totalVolume.length === 0 ? (
                <Text color="secondary">No volume data available. Configure Total Volume first.</Text>
              ) : (
                <>
                  <Row alignmentHorizontal="end" alignmentVertical="center" spacing="300" width="100%">
                    <ProcessLegend />
                    <div style={{ flex: 1 }} />
                    <div className="vd-view-toggle" role="group" aria-label="Group by">
                      <button
                        type="button"
                        className="vd-view-tab"
                        aria-pressed={groupBy === 'process'}
                        onClick={() => setGroupBy('process')}>
                        By Process
                      </button>
                      <button
                        type="button"
                        className="vd-view-tab"
                        aria-pressed={groupBy === 'cycle'}
                        onClick={() => setGroupBy('cycle')}>
                        By Cycle
                      </button>
                    </div>
                    {isOverridden && onRevert && <RevertButton onClick={onRevert} />}
                  </Row>

                  <MatrixView
                    intervals={intervals}
                    availableCycles={availableCycles}
                    totalVolume={totalVolume}
                    distributions={volumeDistributions}
                    onPercentageChange={handlePercentageChange}
                    processEvents={processEvents}
                    readOnly={readOnly}
                    groupBy={groupBy}
                    actualsByCycle={actualsByCycle}
                    endedByInterval={showActuals ? endedByInterval : []}
                  />
                </>
              )}
            </Column>
          </Expander>
        </Column>
        {!isExpanded && isOverridden && onRevert && <RevertButton onClick={onRevert} compact />}
      </Row>
    </ConfigPanel>
  );
};

export default VolumeDistribution;
